/**
 * Helpers for the external-flow enclosure face assignment system.
 *
 * The blockMesh domain box always has 6 axis-aligned faces. Each face is
 * identified by its outward unit normal. The dominant component of the normal
 * identifies the face unambiguously (as required by the backend API).
 *
 * Default classification is derived from the selected flow_axis. The user can
 * override individual faces. Unspecified faces fall back to auto-classification
 * from flow_axis (per backend API contract).
 */

import type { DirectionKey } from '../constants';

export type BoundaryType =
  | 'inlet'
  | 'outlet'
  | 'wall'
  | 'symmetry'
  | 'farfield';

export type FaceKey =
  | '-1,0,0'
  | '1,0,0'
  | '0,-1,0'
  | '0,1,0'
  | '0,0,-1'
  | '0,0,1';

export interface EnclosureFaceAssignment {
  normal: [number, number, number];
  boundary_type: BoundaryType;
}

export interface FaceDefinition {
  key: FaceKey;
  normal: [number, number, number];
  label: string;
}

export const ALL_FACES: FaceDefinition[] = [
  { key: '-1,0,0', normal: [-1, 0, 0], label: '-X' },
  { key: '1,0,0', normal: [1, 0, 0], label: '+X' },
  { key: '0,-1,0', normal: [0, -1, 0], label: '-Y' },
  { key: '0,1,0', normal: [0, 1, 0], label: '+Y' },
  { key: '0,0,-1', normal: [0, 0, -1], label: '-Z' },
  { key: '0,0,1', normal: [0, 0, 1], label: '+Z' },
];

const FACE_BY_KEY = new Map<FaceKey, FaceDefinition>(
  ALL_FACES.map((f) => [f.key, f]),
);

const UPSTREAM_KEY: Record<'x' | 'y' | 'z', FaceKey> = {
  x: '-1,0,0',
  y: '0,-1,0',
  z: '0,0,-1',
};
const DOWNSTREAM_KEY: Record<'x' | 'y' | 'z', FaceKey> = {
  x: '1,0,0',
  y: '0,1,0',
  z: '0,0,1',
};

export function defaultBoundaryType(
  faceKey: FaceKey,
  flowAxis: 'x' | 'y' | 'z',
): BoundaryType {
  if (faceKey === UPSTREAM_KEY[flowAxis]) return 'inlet';
  if (faceKey === DOWNSTREAM_KEY[flowAxis]) return 'outlet';
  return 'wall';
}

// Bridge between the per-direction factor world (`x_neg`, `x_pos`, ...) and
// the face-normal world (`-1,0,0`, `1,0,0`, ...). Same six entries, just two
// different identifier conventions used by different parts of the UI.
const DIRECTION_KEY_TO_FACE_KEY: Record<DirectionKey, FaceKey> = {
  x_neg: '-1,0,0',
  x_pos: '1,0,0',
  y_neg: '0,-1,0',
  y_pos: '0,1,0',
  z_neg: '0,0,-1',
  z_pos: '0,0,1',
};

export function directionKeyToFaceKey(key: DirectionKey): FaceKey {
  return DIRECTION_KEY_TO_FACE_KEY[key];
}

export type FlowRole = 'Upstream' | 'Downstream' | 'Lateral';

/**
 * Returns the high-level flow role for a per-direction key, given the
 * current flow axis and any user overrides on the enclosure faces.
 *
 * 'inlet'  -> 'Upstream'
 * 'outlet' -> 'Downstream'
 *  other   -> 'Lateral'
 *
 * Used to annotate the −X/+X/... labels in EnclosureSettings (relative
 * mode) so users can see at a glance which direction is the inflow vs
 * outflow vs side wall.
 */
export function directionFlowRole(
  key: DirectionKey,
  flowAxis: 'x' | 'y' | 'z',
  overrides: Partial<Record<FaceKey, string>>,
): FlowRole {
  const faceKey = directionKeyToFaceKey(key);
  const label = overrides[faceKey] ?? defaultBoundaryType(faceKey, flowAxis);
  if (label === 'inlet') return 'Upstream';
  if (label === 'outlet') return 'Downstream';
  return 'Lateral';
}

export const VALID_BOUNDARY_TYPES = new Set<string>([
  'wall',
  'inlet',
  'outlet',
  'farfield',
  'symmetry',
]);

export function toBoundaryType(s: string): BoundaryType {
  return VALID_BOUNDARY_TYPES.has(s) ? (s as BoundaryType) : 'wall';
}

// Shared palette for enclosure face coloring — both WebGPU preview and UI text.
// ABGR is the WebGPU byte order (alpha, blue, green, red).
// Known labels get fixed slots; unknown labels hash into the remaining slots.
interface PaletteEntry {
  r: number;
  g: number;
  b: number;
  a: number;
}

// Palette mirrors the renderer's highlightMap gem colors (indices 4–10 in
// the renderer palette texture). ABGR byte order; alpha 0x90 for enclosure
// face semi-transparency. Known labels get fixed slots 0–4; custom labels
// hash into slots 5–9. The flow-arrow glyph colors (ARROW_COLOR_INLET /
// ARROW_COLOR_OUTLET in mesh-viewer/renderer/renderer.ts) mirror the
// inlet/outlet RGB here at full alpha — keep them in sync.
const ENCLOSURE_PALETTE: PaletteEntry[] = [
  { r: 0x90, g: 0xa4, b: 0xb8, a: 0x66 }, // 0 — wall (slate-blue; lifted off the
  //     dark bg + distinct from the amber body wall, inlet-red, outlet-blue)
  { r: 0xff, g: 0x20, b: 0x20, a: 0x80 }, // 1 — inlet
  { r: 0x20, g: 0x20, b: 0xff, a: 0x80 }, // 2 — outlet
  { r: 0x4c, g: 0xc0, b: 0x50, a: 0x80 }, // 3 — farfield
  { r: 0x26, g: 0xa6, b: 0x9a, a: 0x80 }, // 4 — symmetry
  { r: 0xff, g: 0x98, b: 0x00, a: 0x80 }, // 5 — (orange)
  { r: 0x42, g: 0xa5, b: 0xf5, a: 0x80 }, // 6 — (skyblue)
  { r: 0xab, g: 0x47, b: 0xbc, a: 0x80 }, // 7 — (purple)
  { r: 0xec, g: 0x40, b: 0x7a, a: 0x80 }, // 8 — (pink)
  { r: 0xff, g: 0xb3, b: 0x00, a: 0x80 }, // 9 — (amber)
];

const KNOWN_LABEL_INDEX: Record<string, number> = {
  wall: 0,
  inlet: 1,
  outlet: 2,
  farfield: 3,
  symmetry: 4,
  // FEA fixed support shares the solver-view sapphire family.
  fixed: 2,
};

function paletteEntry(label: string): PaletteEntry {
  const known = KNOWN_LABEL_INDEX[label];
  if (known !== undefined) return ENCLOSURE_PALETTE[known];
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = ((hash << 5) - hash + label.charCodeAt(i)) | 0;
  }
  const offset = 5;
  const remaining = ENCLOSURE_PALETTE.length - offset;
  return ENCLOSURE_PALETTE[
    offset + (((hash % remaining) + remaining) % remaining)
  ];
}

export function labelToEnclosureColor(label: string): number {
  const c = paletteEntry(label);
  return (c.a << 24) | (c.b << 16) | (c.g << 8) | c.r;
}

export function labelToCssColor(label: string): string {
  const c = paletteEntry(label);
  return '#' + c.r.toString(16) + c.g.toString(16) + c.b.toString(16);
}

/**
 * Builds the `face_assignments` array for the backend API.
 * Applies user overrides on top of the flow-axis defaults.
 * Free-text labels that don't match a known BoundaryType map to 'wall'.
 */
export function computeFaceAssignments(
  flowAxis: 'x' | 'y' | 'z',
  overrides: Partial<Record<FaceKey, string>>,
): EnclosureFaceAssignment[] {
  return ALL_FACES.map(({ key, normal }) => ({
    normal,
    boundary_type: toBoundaryType(
      overrides[key] ?? defaultBoundaryType(key, flowAxis),
    ),
  }));
}

// The external-flow mesher splits each wind-tunnel lateral into its own polyMesh
// patch (BE feat/split-farfield-laterals, on top of #614). The split keeps the
// lateral's role as the base name + the axis it faces — confirmed from a real
// v2512 `polyMesh/boundary` (patches `wall_ymin/ymax/zmin/zmax`):
//   - `wall_<axis><end>`     (laterals assigned to wall)   → "Wall -Y"
//   - `farfield_<axis><end>` (laterals left as freestream) → "Farfield +Z"
// A numeric `wall_<fid>` form (face-id, no axis) is also handled defensively
// → "Wall 8". The hyphen/plus + uppercase axis matches the ALL_FACES labels.
// Returns null for anything that isn't a recognized split lateral (inlet/outlet,
// a merged single "wall" patch, the body patch, face_tags) so callers fall back
// to the raw patch name.
const ENCLOSURE_AXIS_PATCH = /^(farfield|wall|symmetry)_([xyz])(min|max)$/;
const WALL_FID_PATCH = /^wall_(\d+)$/;
const ENCLOSURE_ROLE_LABEL: Record<string, string> = {
  farfield: 'Farfield',
  wall: 'Wall',
  symmetry: 'Symmetry',
};
export function enclosureFaceDisplayLabel(patchName: string): string | null {
  const m = ENCLOSURE_AXIS_PATCH.exec(patchName);
  if (m) {
    const base = ENCLOSURE_ROLE_LABEL[m[1]];
    return `${base} ${m[3] === 'min' ? '-' : '+'}${m[2].toUpperCase()}`;
  }
  const wall = WALL_FID_PATCH.exec(patchName);
  if (wall) return `Wall ${wall[1]}`;
  return null;
}

// The inlet/outlet pair, which the case writer emits as single merged patches
// rather than per-face (`inlet`, not `inlet_xmin`), plus the bare fallbacks it
// occasionally writes for an un-split box.
const ENCLOSURE_WHOLE_PATCH = new Set([
  'inlet',
  'outlet',
  'farfield',
  'freestream',
]);

/**
 * Is this polyMesh patch part of the wind-tunnel enclosure box, rather than the
 * body being simulated?
 *
 * The one answer to that question, shared by the boundary-conditions panel
 * (which labels these) and the result viewer (which draws them as a box outline
 * instead of a filled surface). Those two used to decide separately, and the
 * viewer's copy only knew the `farfield_*` spelling — so a tunnel whose laterals
 * were assigned `wall` rendered as four opaque slabs around the body (ENG-1480).
 *
 * Matching is deliberately anchored to the `<role>_<axis><end>` shape the case
 * writer reserves for box faces. A BARE `wall` is NOT an enclosure patch: that
 * is what an untagged body resolves to, and on the tet-enclosure path it is the
 * body's own patch name. Treating it as enclosure would hide the part.
 *
 * Nor is a NUMERIC `wall_<n>`, even though `enclosureFaceDisplayLabel` above
 * renders one. `_build_boundary_block` only ever suffixes a box face with an
 * axis (`wall_ymin`) — the numeric form is meshgen's duplicate-label collision
 * suffix (`{label}_{idx:03d}`) applied to a body's `wall` group, and the cfMesh
 * path ships it to the polyMesh unprefixed. So `wall_000` is the part, and
 * matching it here would hide exactly what the viewer exists to show. A wrong
 * guess is cheap in the label helper (one mislabelled row) and expensive here.
 */
export function isEnclosurePatchName(patchName: string): boolean {
  const name = patchName.toLowerCase();
  if (ENCLOSURE_WHOLE_PATCH.has(name)) return true;
  return ENCLOSURE_AXIS_PATCH.test(name);
}

const AXIS_END_TO_FACE_KEY: Record<string, FaceKey> = {
  xmin: '-1,0,0',
  xmax: '1,0,0',
  ymin: '0,-1,0',
  ymax: '0,1,0',
  zmin: '0,0,-1',
  zmax: '0,0,1',
};

// Maps an enclosure polyMesh patch name to the wind-tunnel box FaceKey(s) it
// occupies, so the PRIMARY (`/patches`) path can light up the same BC-panel ↔
// enclosure-box hover/highlight link that `mergeEnclosurePatches` provides on
// the fallback path. `inlet`/`outlet` → the flow-axis upstream/downstream face;
// `(farfield|wall)_<axis><end>` → the single matching lateral. Everything else
// (body_wall, body_Face_N, a merged bare "wall", BL/body face_tags) returns []
// — those aren't enclosure box faces, so they get no overlay.
export function patchNameToFaceKeys(
  patchName: string,
  flowAxis: 'x' | 'y' | 'z',
): FaceKey[] {
  if (patchName === 'inlet') return [UPSTREAM_KEY[flowAxis]];
  if (patchName === 'outlet') return [DOWNSTREAM_KEY[flowAxis]];
  const m = ENCLOSURE_AXIS_PATCH.exec(patchName);
  if (m) {
    const key = AXIS_END_TO_FACE_KEY[`${m[2]}${m[3]}`];
    return key ? [key] : [];
  }
  return [];
}

// Exact world-space enclosure box bounds `[xMin,yMin,zMin,xMax,yMax,zMax]` read
// off the 6 box-face patch centroids: each box-face patch centroid lies on its
// boundary plane, so `centroid[axisOfNormal]` IS that bound — matching the
// blockMesh domain exactly, with no domain-factor approximation. Returns null
// unless all 6 box faces (inlet/outlet + 4 laterals) are present WITH centroids,
// so callers fall back to the factor-synthesized box for a partial / non-meshed
// domain. Body patches (no FaceKey) are ignored.
export function meshedEnclosureBounds(
  boxFaces: ReadonlyArray<{
    name: string;
    centroid?: readonly number[] | null;
  }>,
  flowAxis: 'x' | 'y' | 'z',
): [number, number, number, number, number, number] | null {
  const b: (number | null)[] = [null, null, null, null, null, null];
  for (const f of boxFaces) {
    if (!f.centroid) continue;
    const keys = patchNameToFaceKeys(f.name, flowAxis);
    if (keys.length !== 1) continue; // single-face box patches only
    const face = FACE_BY_KEY.get(keys[0]);
    if (!face) continue;
    const axis = face.normal.findIndex((n) => n !== 0);
    const slot = face.normal[axis] > 0 ? axis + 3 : axis; // min slots 0-2, max 3-5
    b[slot] = f.centroid[axis];
  }
  return b.every((v) => v != null)
    ? (b as [number, number, number, number, number, number])
    : null;
}

// The face-tag label a user assigns (e.g. "Face 8") is emitted by the BE as a
// body-prefixed, space-sanitized polyMesh patch ("body_Face_8") in /patches +
// boundary_conditions (bc_patch_resolution Rule 3). Forward-map a label to that
// patch name so FE joins keyed by patch name (roleByPatch, BL-face detection)
// resolve a face-tag opening. Forward-only (label→patch) is reliable; the
// reverse (patch→label) isn't, since the original spaces are lost.
export function bodyPatchName(faceTagLabel: string): string {
  return `body_${faceTagLabel.replace(/\s+/g, '_')}`;
}

// The axis label ("-X"/"+X"/"-Y"/…) for an enclosure box patch — derived from
// WHICH box face it occupies (its FaceKey), not its name, so it works for
// inlet/outlet (named by role) as well as the wall_/farfield_ laterals. Returns
// null for non-enclosure patches (body_*, merged "wall"). Callers prefix the
// current role to get the full row name, e.g. "Wall -X" / "Symmetry +Y".
export function faceKeyAxisLabel(
  patchName: string,
  flowAxis: 'x' | 'y' | 'z',
): string | null {
  const keys = patchNameToFaceKeys(patchName, flowAxis);
  if (keys.length !== 1) return null;
  return FACE_BY_KEY.get(keys[0])?.label ?? null;
}

const AXIS_LETTER = ['X', 'Y', 'Z'] as const;

// The signed-axis label ("-Z"/"+X"/…) for an enclosure face identified by its
// outward normal — the dominant (largest-magnitude) component picks the axis,
// its sign the end. The normal-keyed counterpart to `faceKeyAxisLabel`, for the
// `domain.face_assignments` path where faces carry a normal, not a patch name.
// Returns null for a degenerate (all-zero) normal.
export function axisLabelFromNormal(
  normal: readonly [number, number, number],
): string | null {
  let axis = 0;
  for (let i = 1; i < 3; i++) {
    if (Math.abs(normal[i]) > Math.abs(normal[axis])) axis = i;
  }
  if (normal[axis] === 0) return null;
  return `${normal[axis] > 0 ? '+' : '-'}${AXIS_LETTER[axis]}`;
}
